import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";

import Login from "@/components/Login";
import Register from "@/components/Register";
import VerifyEmail from "@/components/VerifyEmail";
import ForgotPassword from "@/components/ForgotPassword";
import ResetPassword from "@/components/ResetPassword";
import ApproveAreaManager from "@/components/ApproveAreaManager";
import Positions from "@/components/Positions";
import Layout from "@/components/Layout";
import Dashboard from "@/components/Dashboard";
import Employees from "@/components/Employees";
import Branches from "@/components/Branches";
import Admins from "@/components/Admins";
import Calendar from "@/components/Calendar";
import AreaManagers from "@/components/AreaManagers";
import Profile from "@/components/Profile";
import AuditLog from "@/components/AuditLog";
import LicensesPreview from "@/components/preview/LicensesPreview";
import ModuleChooser from "@/components/ModuleChooser";
import LicensesTracker from "@/components/LicensesTracker";
import DayOffTracker from "@/components/DayOffTracker";
import EmployeeManagement from "@/components/EmployeeManagement";
import { EMProfile } from "@/components/EMSections";
import SystemSettings from "@/components/SystemSettings";
import ModuleAlertsPage from "@/components/ModuleAlertsPage";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LanguageProvider } from "@/lib/i18n";
import { Toaster } from "sonner";

function RequireAuth({ children }) {
  const { user, bootstrapped } = useAuth();
  if (!bootstrapped) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireSuperAdmin({ children }) {
  const { user, bootstrapped } = useAuth();
  if (!bootstrapped) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <LanguageProvider>
      <Toaster position="top-center" richColors closeButton />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin/approve" element={<ApproveAreaManager />} />
            <Route
              path="/preview/licenses"
              element={
                <RequireAuth>
                  <LicensesPreview />
                </RequireAuth>
              }
            />
            <Route
              path="/choose"
              element={
                <RequireAuth>
                  <ModuleChooser />
                </RequireAuth>
              }
            />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="employees" element={<Employees />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="branches" element={<Branches />} />
              <Route path="admins" element={<Admins />} />
              <Route path="profile" element={<Profile />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="alerts/:module" element={<ModuleAlertsPage />} />
              <Route path="licenses" element={<LicensesTracker />} />
              <Route path="dayoff" element={<DayOffTracker />} />
              <Route path="employee-management" element={<EmployeeManagement />} />
              <Route path="employee-management/employees/:empId" element={<EMProfile />} />
              <Route path="employee-management/:section" element={<EmployeeManagement />} />
              <Route
                path="system-settings"
                element={
                  <RequireSuperAdmin>
                    <SystemSettings />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="area-managers"
                element={
                  <RequireSuperAdmin>
                    <AreaManagers />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="positions"
                element={
                  <RequireSuperAdmin>
                    <Positions />
                  </RequireSuperAdmin>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </LanguageProvider>
  );
}
